import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { GlobalButton } from './GlobalButton';
import { Favicon } from '../utils/favicon';
import { useLocale } from '../context/DynamicLocaleProvider';
import { db } from '../lib/firebase';
import { getProjectById } from '../app/project/[projectId]/controller';
import { getCompanyById } from '../app/company/[companyId]/controller';
import { buildThesconAiPath } from '../utils/thesconAiNavigation';
import LocalLLMTrust from './LocalLLMTrust';
import styles from './GlobalGptAccessPanel.module.css';

export interface GlobalGptAccessPanelProps {
  /** When set without `projectId`, opens GPT scoped to this company’s knowledge (same as `knowledgeId` in URL). */
  companyId?: string;
  projectId?: string;
  /** Explicit knowledge collection id (e.g. document view). Overrides `companyId` for URL when both would apply. */
  knowledgeId?: string;
  hideBorderTop?: boolean;
  className?: string;
}

export default function GlobalGptAccessPanel({
  companyId,
  projectId,
  knowledgeId,
  hideBorderTop = false,
  className = '',
}: GlobalGptAccessPanelProps) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDisplayName('');
    setCompanyWebsite(null);

    async function load() {
      if (projectId) {
        try {
          const project = await getProjectById(projectId, t);
          if (cancelled || !project) return;
          setDisplayName(project.projectName || '');
          if (project.companyId) {
            try {
              const companyRef = doc(db, 'companies', project.companyId);
              const companyDoc = await getDoc(companyRef);
              if (!cancelled && companyDoc.exists()) {
                const data = companyDoc.data();
                setCompanyWebsite(data.website || null);
              }
            } catch {
              if (!cancelled) setCompanyWebsite(null);
            }
          }
        } catch {
          if (!cancelled) {
            setDisplayName('');
            setCompanyWebsite(null);
          }
        }
        return;
      }

      if (companyId) {
        try {
          const company = await getCompanyById(companyId, t);
          if (cancelled || !company) return;
          setDisplayName(company.name || '');
          setCompanyWebsite(company.website || null);
        } catch {
          if (!cancelled) {
            setDisplayName('');
            setCompanyWebsite(null);
          }
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, companyId, t]);

  const pageTitle = useMemo(() => {
    if (projectId && displayName) return `${displayName} GPT`;
    if (companyId && displayName) {
      return t('global_gpt_access_panel_headline_company').replace('{{name}}', displayName);
    }
    return t('thescon_ai_title');
  }, [projectId, companyId, displayName, t]);

  const href = useMemo(
    () => buildThesconAiPath({ projectId, companyId, knowledgeId }),
    [projectId, companyId, knowledgeId]
  );

  const ctaLabel = useMemo(() => {
    if ((projectId || companyId) && displayName) {
      return t('global_gpt_access_panel_cta_named').replace('{{name}}', displayName);
    }
    return t('global_gpt_access_panel_cta');
  }, [projectId, companyId, displayName, t]);

  const bullets = [
    t('global_gpt_access_panel_bullet_1'),
    t('global_gpt_access_panel_bullet_2'),
    t('global_gpt_access_panel_bullet_3'),
  ];

  const showFavicon =
    Boolean(companyWebsite) && (Boolean(projectId) || Boolean(companyId));

  return (
    <div
      className={`${styles.panel} ${hideBorderTop ? styles.noBorderTop : ''} ${className}`.trim()}
    >
      <div className={styles.header}>
        {showFavicon && (
          <div className={styles.iconWrap}>
            <Favicon websiteUrl={companyWebsite!} size={24} retinaMultiplier={2} />
          </div>
        )}
        <h2 className={styles.title}>{pageTitle}</h2>
      </div>
      <p className={styles.intro}>{t('global_gpt_access_panel_intro')}</p>
      <ul className={styles.bullets}>
        {bullets.map((text, i) => (
          <li key={i}>{text}</li>
        ))}
      </ul>
      <div className={styles.cta}>
        <GlobalButton
          type="button"
          autoWidth
          iconPosition="right"
          onClick={() => navigate(href)}
        >
          {ctaLabel}
        </GlobalButton>
      </div>
      <div className={styles.trustFooter}>
        <LocalLLMTrust />
      </div>
    </div>
  );
}
